import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SettingsPage from './Settings';

const focusableSelector = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

const SettingsOverlay = ({ isOpen, onClose, ...settingsProps }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement;
    const dialog = dialogRef.current;
    const activeDialog = () => dialog.querySelector('[role="alertdialog"]') || dialog;
    const targets = () => Array.from(activeDialog().querySelectorAll(focusableSelector))
      .filter(element => element.getClientRects().length && element.getAttribute('aria-hidden') !== 'true');
    targets()[0]?.focus();
    const handleKeyDown = event => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current?.(); }
      if (event.key === 'Tab') {
        const elements = targets();
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (!first) { event.preventDefault(); activeDialog().focus(); }
        else if (event.shiftKey && (document.activeElement === first || !activeDialog().contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !activeDialog().contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    const containFocus = event => {
      if (!activeDialog().contains(event.target)) (targets()[0] || activeDialog()).focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', containFocus);
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', containFocus);
      document.body.style.overflow = previousBodyOverflow;
      if (opener?.isConnected) opener.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return createPortal(
    <div className="settings-overlay">
      <button type="button" className="settings-backdrop" onClick={onClose} tabIndex={-1} aria-label="Close settings" />
      <div ref={dialogRef} className="settings-window theme-panel-bg" role="dialog" aria-modal="true" aria-label="Settings" tabIndex={-1}>
        <SettingsPage {...settingsProps} onBackToChat={onClose} />
      </div>
    </div>, document.body,
  );
};

export default SettingsOverlay;
